import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useSite } from "@/context/SiteContext";
import { EnquiryDialog } from "@/components/EnquiryDialog";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, Phone } from "lucide-react";
import logo from "@/assets/logo.png";

const NAV = [
  { to: "/domestic", label: "Domestic Lawncare" },
  { to: "/commercial", label: "Commercial Mowing" },
  { to: "/line-marking", label: "Line Marking" },
  { to: "/clubs", label: "Clubs & Sports" },
  { to: "/service", label: "Service & Repairs" },
  { to: "/about", label: "About Us" },
  { to: "/contact", label: "Contact" },
];

export function Header() {
  const site = useSite();
  const [assessOpen, setAssessOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  const go = (to) => {
    setMobileOpen(false);
    navigate(to);
  };

  return (
    <header className="sticky top-0 z-50 bg-brand-navy/95 backdrop-blur-md border-b border-white/10 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 lg:h-20 items-center justify-between gap-4">
          <Link to="/" className="flex items-center shrink-0" data-testid="header-logo">
            <span className="bg-white rounded-md p-1 shadow-sm">
              <img src={logo} alt="Robotic Lawnmower Experts" className="h-10 lg:h-12 w-auto block" />
            </span>
          </Link>

          <nav className="hidden xl:flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.to.replace("/", "")}`}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive ? "bg-white/15 text-white" : "text-white/85 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3 shrink-0">
            <a href={`tel:${site.phone?.replace(/\s/g, "")}`} className="flex items-center gap-2 text-sm font-medium text-white/90 hover:text-white transition-colors" data-testid="header-phone">
              <Phone className="h-4 w-4" /> {site.phone}
            </a>
            <Button onClick={() => setAssessOpen(true)} className="bg-white hover:bg-white/90 text-brand-navy font-semibold" data-testid="header-assessment-button">
              Book a Site Assessment
            </Button>
          </div>

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild className="xl:hidden">
              <Button variant="ghost" size="icon" className="text-white hover:bg-white/10" data-testid="mobile-menu-button">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-brand-navy text-white border-l border-white/10 w-[85vw] max-w-sm overflow-y-auto">
              <div className="mt-8 flex flex-col gap-1">
                {NAV.map((n) => (
                  <button key={n.to} onClick={() => go(n.to)} className="text-left px-3 py-3 rounded-md text-base font-medium text-white/90 hover:bg-white/10 transition-colors" data-testid={`mobile-nav-${n.to.replace("/", "")}`}>
                    {n.label}
                  </button>
                ))}
                <div className="mt-4 flex flex-col gap-3">
                  <a href={`tel:${site.phone?.replace(/\s/g, "")}`} className="flex items-center gap-2 px-3 text-white/90">
                    <Phone className="h-4 w-4" /> {site.phone}
                  </a>
                  <Button onClick={() => { setMobileOpen(false); setAssessOpen(true); }} className="bg-white hover:bg-white/90 text-brand-navy font-semibold">
                    Book a Site Assessment
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      <EnquiryDialog open={assessOpen} onOpenChange={setAssessOpen} type="assessment" sourcePage="header" />
    </header>
  );
}
